import { Box, Tooltip, Typography } from '@mui/material';
import { useMemo } from 'react';

import { ConceptGroupChip } from '@verantos-dev/shared-components';

import { ConceptGroupAddButton } from './ConceptGroupAddButton';
import { getConceptGroupsForAttribute } from './conceptGroupDomainFilter';
import type { ConceptGroupOption } from '../../types';
import type { ConceptRefRow } from '../../utils/analyticalVariable.utils';
import { extractConceptNames } from '../../utils/analyticalVariable.utils';
import type { ConceptGroupsByDomain } from '../AnalyticalVariableCard/AnalyticalVariableCardContext';

interface ConceptRefRowRendererProps {
  row: ConceptRefRow;
  onChipClick: (name: string) => void;
  conceptGroupIdToNameMap: Map<string, string>;
  conceptGroupNameToOwnerMap: Map<string, string | undefined>;
  unavailableTooltip: string;
  isEditable: boolean;
  isSaving?: boolean;
  savingTooltip?: string;
  availableConceptGroups: ConceptGroupOption[];
  conceptGroupsByDomain?: ConceptGroupsByDomain;
  onAdd?: (name: string) => void;
  onRemove?: (name: string) => void;
  onDropdownClose?: () => void;
  editedNames?: string[];
  isConceptGroupsLoading?: boolean;
  forceOpenDropdown?: boolean;
}

export const ConceptRefRowRenderer = ({
  row,
  onChipClick,
  conceptGroupIdToNameMap,
  conceptGroupNameToOwnerMap,
  unavailableTooltip,
  isEditable,
  isSaving = false,
  savingTooltip,
  availableConceptGroups,
  conceptGroupsByDomain,
  onAdd,
  onRemove,
  onDropdownClose,
  editedNames,
  isConceptGroupsLoading = false,
  forceOpenDropdown = false,
}: ConceptRefRowRendererProps) => {
  const canInteract = isEditable && !isSaving;
  const names = editedNames ?? extractConceptNames(row.conceptRef, conceptGroupIdToNameMap);

  const domainFilteredGroups = useMemo(
    () =>
      getConceptGroupsForAttribute(
        row.conceptAttribute,
        conceptGroupsByDomain,
        availableConceptGroups
      ),
    [row.conceptAttribute, conceptGroupsByDomain, availableConceptGroups]
  );

  if (names.length === 0 && !isEditable) return null;

  return (
    <Box
      data-testid="av-logic-concept-ref-row"
      sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}
    >
      <Typography data-testid="av-logic-concept-ref-label" variant="body2" color="text.primary">
        {row.label}
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
        {names.map((name, idx) => {
          const isInProject = conceptGroupNameToOwnerMap.has(name.toLowerCase());
          return (
            <ConceptGroupChip
              key={`${name}-${idx}`}
              name={name}
              owner={conceptGroupNameToOwnerMap.get(name.toLowerCase())}
              clickable={isInProject}
              unavailableTooltip={!isInProject ? unavailableTooltip : undefined}
              onClick={onChipClick}
              onDelete={canInteract ? onRemove : undefined}
              loading={isConceptGroupsLoading}
            />
          );
        })}
        {isEditable && (
          <Tooltip title={isSaving ? savingTooltip : ''} arrow placement="top">
            <span>
              <ConceptGroupAddButton
                availableConceptGroups={domainFilteredGroups}
                selectedNames={names}
                onSelect={(name) => onAdd?.(name)}
                onClose={onDropdownClose}
                onPreview={onChipClick}
                disabled={isSaving}
                forceOpen={forceOpenDropdown}
              />
            </span>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
};
